/**
 * @module lib/election/service
 */
import logger from "utils/logger";
import { validStatus, handleJSON } from "utils/fetch";
import { electionLeadingUrl, electionRidingUrl } from "lib/election-services";

import ee from "lib/eventemitter";
import { getById, getProp, qs } from "utils/utils";
import tgamConfig from "config/tgam";
import { local } from "lib/store";

const debugTag = "[ELECTION-SERVICE]";
const electionYear = getProp(tgamConfig, "meta.electionYear");
let leadingData = null;
let ridingDataObject = null;
let ridingDataArray = null;

/**
 * Fetch the election riding data and emit events when it's available
 */
export default function initElectionService() {
  getElectionData();
}

/**
 * Returns the leading data that was fetched from the API
 * @returns {Object} Leading data
 */
export function getFetchedLeadingData() {
  return leadingData;
}

/**
 * Returns the riding data that was fetched from the API
 * @returns {Object} Riding data
 */
export function getFetchedRidingDataObject() {
  return ridingDataObject;
}

/**
 * Returns the riding data that was fetched from the API, in array format
 * @returns {Array<Object>} An array of riding objects
 */
export function getFetchedRidingDataArray() {
  return ridingDataArray;
}

/**
 * Get the election data and emit an event for other features to listen to (in order to refresh).
 */
function getElectionData() {
  logger.info(`${debugTag} getElectionData`);

  const leadingDataPromise = getLeadingData().then(data => {
    leadingData = data;
    ee.trigger("election:leading-data", [data]);
    return data;
  }).catch(reason => {
    logger.info(`${debugTag} No Leading Data: ${reason}`);
    ee.trigger("election:leading-data-failed");
  });

  const ridingDataPromise = getRidingData().then(data => {
    const dataArray = convertRidingDataToArray(data);
    ridingDataObject = data;
    ridingDataArray = dataArray;
    ee.trigger("election:riding-data", [data, dataArray]);
    return [data, dataArray];
  }).catch(reason => {
    logger.info(`${debugTag} No Riding Data: ${reason}`);
    ee.trigger("election:riding-data-failed");
  });

  Promise.all([leadingDataPromise, ridingDataPromise]).then(([leadingData, ridingData]) => {
    const [ridingDataObject, ridingDataArray] = ridingData;
    ee.trigger("election:all-data", [leadingData, ridingDataObject, ridingDataArray]);
    logger.info(`${debugTag} All Data loaded`);
  }).catch(reason => {
    logger.info(`${debugTag} No Data: ${reason}`);
    ee.trigger("election:all-data-failed");
  });
}

/**
 * Fetch the leading data.
 * @returns {Object} leading data.
 */
function getLeadingData() {
  return fetch(electionLeadingUrl(electionYear))
    .then(validStatus)
    .then(handleJSON)
    .then(data => data.Election);
}

/**
 * Fetch the riding data.
 * @returns {Object} riding data.
 */
function getRidingData() {
  return fetch(electionRidingUrl(electionYear))
    .then(validStatus)
    .then(handleJSON);
}

/**
 * Convert a riding data object into an array, as follows:
 * {
 *   "10001": { "TotalPolls": "233" },
 *   "10002": { "TotalPolls": "108" }
 * }
 * will become:
 * [
 *   { "id": "10001", "TotalPolls": "233" },
 *   { "id": "10002", "TotalPolls": "108" }
 * ]
 * @param {Object} data - Riding data that was fetched from the API
 * @returns {Array<Object>} An array of riding objects
 */
function convertRidingDataToArray(data) {
  if (!data) {
    return [];
  }
  return Object.entries(data).map(([key, value]) => {
    return Object.assign({ id: key }, value);
  });
}

/**
 * Fetch election data every 30s by default and if checkbox is checked, save checkbox status to local storage
 */
export function initElectionRefresh() {
  const checkbox = getById("c-election-live-results-checkbox");
  const electionYear = getProp(tgamConfig, "meta.electionYear", "2021");

  if (!checkbox || electionYear === "2019") { // No need to refresh/refetch 2019 archived data.
    return;
  }

  const electionRefreshEnabled = getProp(tgamConfig, "env.electionRefreshEnabled") === "true";
  const refreshCheckboxElement = qs(".c-election-live-results__refresh");
  if (!electionRefreshEnabled && refreshCheckboxElement) {
    refreshCheckboxElement.classList.add("u-hidden");
    local.remove("tgam.refreshElection");
    return;
  }
  const countdown = getById("js-election-refresh-countdown");
  const shouldRefresh = local.has("tgam.refreshElection") ? local.get("tgam.refreshElection") : true;
  const intervalSeconds = Number(getProp(tgamConfig, "env.electionRefreshInterval", 30));
  const interval = intervalSeconds * 1000;
  let counter;
  let count = intervalSeconds;
  let refresh;

  if (shouldRefresh) {
    checkbox.checked = true;
    activateElectionRefresh();
  } else {
    checkbox.checked = false;
    deactivateElectionRefresh();
  }

  checkbox && checkbox.addEventListener("click", () => {
    local.set("tgam.refreshElection", checkbox.checked);
    if (checkbox.checked) {
      activateElectionRefresh();
    } else {
      deactivateElectionRefresh();
    }
  });

  function activateElectionRefresh() {
    if (!countdown) {
      return;
    }
    updateCounterElement();

    counter = window.setInterval(() => {
      if (count > 0) {
        count--;
      }
      updateCounterElement();
    }, 1000);

    refresh = window.setInterval(() => {
      count = intervalSeconds;
      getElectionData();
    }, interval);
  }

  function deactivateElectionRefresh() {
    window.clearInterval(counter);
    window.clearInterval(refresh);
    count = intervalSeconds;
  }

  function updateCounterElement() {
    window.requestAnimationFrame(() => {
      countdown.title = `${count} seconds`;
      countdown.textContent = `${count}s`;
    });
  }
}
